import { fetchUserNotice, type NoticeResponse } from '@/services/api/userNotice/userNotice'
import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Pinia store for managing user notices with caching
 *
 * This store fetches and caches user notices from the API with a 10-minute
 * time-to-live (TTL). It implements request deduplication to prevent
 * multiple simultaneous API calls and persists state to sessionStorage.
 *
 * @example
 * ```typescript
 * const noticeStore = useNoticeStore()
 * const { state, loaded } = await noticeStore.getStore()
 * if (state) {
 *   console.log('Notice:', state)
 * }
 * ```
 */
export const useNoticeStore = defineStore(
	'userNotice',
	() => {
		// ====---------------====
		// 🗄️ State
		// ====---------------====

		/** The current notice message, or null if no notice exists */
		const state = ref<string | null>(null)

		/** Whether the notice has been successfully loaded from the API */
		const loaded = ref<boolean>(false)

		/** Timestamp of the last successful API fetch */
		const lastChecked = ref<null | Date>(null)

		// ====---------------====
		// 🏃‍♂️ Getters
		// ====---------------====

		/**
		 * Determines if the cached notice has exceeded the 10-minute TTL
		 *
		 * @returns {boolean} True if cache is stale or never checked, false if still fresh
		 */
		function overTimeLimit(): boolean {
			if (!lastChecked.value) return true // Should fetch if never checked
			const now = Date.now()
			const lastCheck = new Date(lastChecked.value).getTime()
			const TEN_MINUTES_MS = 10 * 60 * 1000
			return now - lastCheck >= TEN_MINUTES_MS
		}

		/**
		 * Retrieves the notice store state, fetching from API if cache is stale
		 *
		 * This method automatically handles cache invalidation and will fetch
		 * fresh data if:
		 * - The store has never been loaded
		 * - The last check timestamp is missing
		 * - More than 10 minutes have passed since the last check
		 *
		 * @returns {Promise<{state: string | null, loaded: boolean}>} The current store state
		 * @throws Will log errors to console but always returns current state
		 *
		 * @example
		 * ```typescript
		 * const { state, loaded } = await noticeStore.getStore()
		 * if (loaded && state) {
		 *   displayNotice(state)
		 * }
		 * ```
		 */
		async function getStore(): Promise<{
			state: string | null
			loaded: boolean
		}> {
			try {
				if (!loaded.value || !lastChecked.value || overTimeLimit()) {
					await loadStore()
				}
				return {
					state: state.value,
					loaded: loaded.value
				}
			} catch (error) {
				console.error('Failed to load user notice:', error)
				return {
					state: state.value,
					loaded: loaded.value
				}
			}
		}

		// ====---------------====
		// 🎬 Actions
		// ====---------------====

		/**
		 * Resets the store to its initial state
		 *
		 * Clears all cached data including the notice message, loaded flag,
		 * and last checked timestamp. Useful for logout scenarios or when
		 * forcing a fresh fetch is required.
		 */
		function $reset(): void {
			state.value = null
			loaded.value = false
			lastChecked.value = null
		}

		/**
		 * Tracks in-flight API requests to prevent duplicate calls
		 * @private
		 */
		let loadStoreRequest: Promise<void> | null = null

		/**
		 * Fetches user notice from the API with request deduplication
		 *
		 * This function implements a singleton pattern to ensure only one
		 * API request is in flight at a time. If called while a request is
		 * already pending, it returns the existing Promise instead of making
		 * a new request.
		 *
		 * @returns {Promise<void>} Resolves when the notice is loaded
		 * @throws {Error} If the API response format is invalid
		 * @throws {Error} If the API request fails
		 *
		 * @private
		 * @example
		 * ```typescript
		 * // Multiple simultaneous calls will share the same request
		 * await Promise.all([
		 *   loadStore(),
		 *   loadStore(),
		 *   loadStore()
		 * ]) // Only makes 1 API call
		 * ```
		 */
		const loadStore = async (): Promise<void> => {
			if (loadStoreRequest) {
				return loadStoreRequest
			}

			loadStoreRequest = (async () => {
				try {
					const res: NoticeResponse = await fetchUserNotice().then((result) => result)
					state.value = res.notice
					loaded.value = true
					lastChecked.value = new Date()
				} finally {
					loadStoreRequest = null
				}
			})()

			return loadStoreRequest
		}

		// ====---------------====
		// 🚀 Exports
		// ====---------------====
		return {
			/** The current notice message */
			state,
			/** Whether the notice has been loaded */
			loaded,
			/** Timestamp of last API fetch */
			lastChecked,
			/** Retrieves store state with automatic cache refresh */
			getStore,
			/** Resets store to initial state */
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
